// src/pages/Clientes.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Clientes.css';
import { 
    FaUserPlus, FaUsers, FaSearch, FaFileImport, FaFileExport, FaDownload, 
    FaTruck, FaCalendarCheck, FaArrowLeft, FaIdCard, FaPhone, FaEdit, 
    FaTrash, FaEye, FaTimes, FaCheckSquare, FaRegSquare, FaTh, FaList // <-- Adicionados FaTh e FaList
} from 'react-icons/fa';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import * as XLSX from 'xlsx';
import ClienteDetalhesModal from '../components/ClienteDetalhesModal';

export default function Clientes() {
    const navigate = useNavigate();
    
    const [clientes, setClientes] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Controle de Modo de Visualização (Grid vs List)
    const [viewMode, setViewMode] = useState('grid'); 

    const [selectedClients, setSelectedClients] = useState([]);

    const initialFormState = { nome: '', telefone: '', email: '', endereco: '', cpf_cnpj: '' };
    const [formData, setFormData] = useState(initialFormState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editingCliente, setEditingCliente] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [viewingClientId, setViewingClientId] = useState(null);

    const fileInputRef = useRef(null);
    const [isImporting, setIsImporting] = useState(false);
    const [userProfile, setUserProfile] = useState(null);

    const fetchUserData = async () => {
        try {
            const { data: userData } = await api.get('/api/usuarios/me');
            setUserProfile(userData.perfil);
        } catch (error) {
            console.error("Erro ao buscar perfil do usuário:", error);
        }
    };
    
    const fetchClientes = async (page = 1) => {
        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const response = await api.get('/api/clients', {
                headers: { 'Authorization': `Bearer ${token}` },
                params: { query: searchQuery, page, limit: 12 }
            });
            setClientes(response.data.data);
            setPagination(response.data.pagination);
            setSelectedClients([]); 
        } catch (error) {
            console.error("Erro ao buscar clientes:", error);
            setError("Falha ao carregar a base de clientes. Tente novamente.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUserData();
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => { fetchClientes(1); }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery]);
    
    const handleOpenViewModal = (id) => { setViewingClientId(id); setIsViewModalOpen(true); };
    const handleCloseViewModal = () => { setIsViewModalOpen(false); setViewingClientId(null); };
    const handleOpenAddModal = () => setIsAddModalOpen(true);
    const handleCloseAddModal = () => { setIsAddModalOpen(false); setFormData(initialFormState); };
    const handleOpenEditModal = (cliente) => { setEditingCliente(cliente); setIsEditModalOpen(true); };
    const handleCloseEditModal = () => { setIsEditModalOpen(false); setEditingCliente(null); };

    const handleToggleSelect = (id) => {
        setSelectedClients(prev => 
            prev.includes(id) ? prev.filter(clientId => clientId !== id) : [...prev, id]
        );
    };

    const handleSelectAll = () => {
        if (selectedClients.length === clientes.length) {
            setSelectedClients([]); 
        } else {
            setSelectedClients(clientes.map(c => c.id)); 
        }
    };

    const handleMassDelete = async () => {
        if (window.confirm(`Atenção: Tem certeza que deseja excluir permanentemente ${selectedClients.length} cliente(s)? Esta ação não pode ser desfeita.`)) {
            setIsLoading(true);
            try {
                await Promise.all(selectedClients.map(id => api.delete(`/api/clients/${id}`)));
                alert('Clientes excluídos com sucesso.');
                fetchClientes(1);
            } catch (error) {
                alert('Aviso: Alguns clientes não puderam ser excluídos pois possuem Ordens de Serviço vinculadas a eles.');
                fetchClientes(pagination.page); 
            }
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Atenção: Tem certeza que deseja excluir este cliente permanentemente?')) {
            try {
                await api.delete(`/api/clients/${id}`);
                const isLastItemOnPage = clientes.length === 1 && pagination.page > 1;
                fetchClientes(isLastItemOnPage ? pagination.page - 1 : pagination.page);
            } catch (error) {
                alert('Erro ao excluir cliente. Verifique se há serviços atrelados a ele.');
            }
        }
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await api.post('/api/clients', formData);
            handleCloseAddModal();
            fetchClientes(1);
        } catch (error) {
            alert('Erro ao cadastrar cliente.');
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await api.put(`/api/clients/${editingCliente.id}`, editingCliente);
            handleCloseEditModal();
            fetchClientes(pagination.page);
        } catch (error) {
            alert('Erro ao atualizar cadastro do cliente.');
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
    const handleEditInputChange = (e) => setEditingCliente({ ...editingCliente, [e.target.name]: e.target.value });

    const handleExport = async (format) => {
        try {
            const response = await api.get(`/api/clients/export?format=${format}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `base_clientes.${format}`); 
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            alert('Erro ao exportar a base de clientes.');
        }
    };
    
    const handleDownloadTemplate = (format) => {
        const header = [ "nome", "telefone", "email", "endereco", "cpf_cnpj" ];
        const exampleRow = [ "João da Silva", "11987654321", "joao@exemplo.com.br", "Rua das Flores, 123", "123.456.789-00" ];
        const fileName = `modelo_importacao.${format}`;

        if (format === 'csv') {
            const csvContent = [header.join(','), exampleRow.join(',')].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url; link.setAttribute('download', fileName);
            document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
        } else if (format === 'xlsx') {
            const worksheet = XLSX.utils.aoa_to_sheet([header, exampleRow]);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Modelo Clientes");
            XLSX.writeFile(workbook, fileName);
        }
    };
    
    const handleImport = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        
        const importFormData = new FormData();
        importFormData.append('file', file);
        setIsImporting(true);
        
        try {
            await api.post('/api/clients/import', importFormData, { headers: { 'Content-Type': 'multipart/form-data' }});
            alert('Importação concluída com sucesso!');
            fetchClientes(1);
        } catch (error) {
            alert(`Erro na importação: ${error.response?.data?.error || 'Arquivo inválido.'}`);
        } finally {
            setIsImporting(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages && newPage !== pagination.page) {
            fetchClientes(newPage);
        }
    };
    
    const hasAdminPermission = userProfile === 'admin_geral' || userProfile === 'admin';

    return (
        <main className="cl-page-wrapper">
            {/* CABEÇALHO */}
            <header className="cl-header">
                <div className="cl-header-title-box">
                    <button onClick={() => navigate(-1)} className="cl-btn-back" aria-label="Voltar">
                        <FaArrowLeft />
                    </button>
                    <div>
                        <h1 className="cl-title">Gestão de Clientes</h1>
                        <p className="cl-subtitle">Gerencie, cadastre e acompanhe o histórico da sua base.</p>
                    </div>
                </div>

                <div className="cl-header-actions">
                    {hasAdminPermission && (
                        <div className="cl-actions-secondary">
                            <div className="cl-dropdown">
                                <button type="button" className="cl-btn cl-btn-outline">
                                    <FaDownload /> Modelo
                                </button>
                                <div className="cl-dropdown-menu">
                                    <button type="button" onClick={() => handleDownloadTemplate('csv')}>.CSV</button>
                                    <button type="button" onClick={() => handleDownloadTemplate('xlsx')}>.XLSX</button>
                                </div>
                            </div>

                            <button 
                                type="button" 
                                onClick={() => fileInputRef.current.click()} 
                                className="cl-btn cl-btn-outline" 
                                disabled={isImporting}
                            >
                                <FaFileImport /> {isImporting ? 'Importando...' : 'Importar'}
                            </button>
                            <input type="file" ref={fileInputRef} onChange={handleImport} style={{ display: 'none' }} accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" />

                            <div className="cl-dropdown">
                                <button type="button" className="cl-btn cl-btn-outline">
                                    <FaFileExport /> Exportar
                                </button>
                                <div className="cl-dropdown-menu">
                                    <button type="button" onClick={() => handleExport('csv')}>.CSV</button>
                                    <button type="button" onClick={() => handleExport('xlsx')}>.XLSX</button>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <button type="button" onClick={handleOpenAddModal} className="cl-btn cl-btn-primary">
                        <FaUserPlus /> Novo Cliente
                    </button>
                </div>
            </header>

            {selectedClients.length > 0 && hasAdminPermission && (
                <div className="cl-mass-action-bar animate-slide-down">
                    <div className="cl-mass-action-info">
                        <span className="cl-mass-count">{selectedClients.length}</span> cliente(s) selecionado(s)
                    </div>
                    <div className="cl-mass-action-buttons">
                        <button onClick={() => setSelectedClients([])} className="cl-btn cl-btn-outline">Cancelar</button>
                        <button onClick={handleMassDelete} className="cl-btn cl-btn-danger">
                            <FaTrash /> Excluir Selecionados
                        </button>
                    </div>
                </div>
            )}
            
            {/* BARRA DE PESQUISA E CONTROLES DE VIEW */}
            <section className="cl-search-section">
                <div className="cl-search-box">
                    <FaSearch className="cl-search-icon" />
                    <input 
                        type="text" 
                        className="cl-search-input"
                        placeholder="Pesquisar por nome, CPF/CNPJ ou telefone..." 
                        value={searchQuery} 
                        onChange={(e) => setSearchQuery(e.target.value)} 
                    />
                </div>
                
                <div className="cl-search-controls">
                    {hasAdminPermission && clientes.length > 0 && !isLoading && (
                        <button onClick={handleSelectAll} className="cl-select-all-btn">
                            {selectedClients.length === clientes.length ? <FaCheckSquare className="text-blue" /> : <FaRegSquare />} 
                            Selecionar Todos
                        </button>
                    )}
                    
                    {/* TOGGLE DE VISUALIZAÇÃO */}
                    <div className="cl-view-toggle">
                        <button 
                            className={`cl-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                            onClick={() => setViewMode('grid')}
                            title="Visualização em Grid"
                        >
                            <FaTh />
                        </button>
                        <button 
                            className={`cl-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                            onClick={() => setViewMode('list')}
                            title="Visualização em Lista"
                        >
                            <FaList />
                        </button>
                    </div>
                </div>
            </section>

            {/* LISTAGEM DE CLIENTES */}
            <section className="cl-content">
                {isLoading ? (
                    <div className="cl-state-box">
                        <div className="cl-spinner"></div>
                        <p>Carregando carteira de clientes...</p>
                    </div>
                ) : error ? (
                    <div className="cl-state-box cl-error">
                        <p>{error}</p>
                    </div>
                ) : clientes.length === 0 ? (
                    <div className="cl-state-box">
                        <FaUsers className="cl-empty-icon" />
                        <p>Nenhum cliente encontrado com os filtros informados.</p>
                    </div>
                ) : (
                    <>
                        {/* RENDERIZAÇÃO CONDICIONAL: GRID OU LISTA */}
                        {viewMode === 'grid' ? (
                            <div className="cl-grid animate-fade-in">
                                {clientes.map(cliente => {
                                    const isSelected = selectedClients.includes(cliente.id);
                                    return (
                                        <article key={cliente.id} className={`cl-card ${isSelected ? 'selected' : ''}`}>
                                            <header className="cl-card-header">
                                                <div className="cl-card-title-group">
                                                    {hasAdminPermission && (
                                                        <div className="cl-card-checkbox" onClick={() => handleToggleSelect(cliente.id)}>
                                                            {isSelected ? <FaCheckSquare className="checked" /> : <FaRegSquare />}
                                                        </div>
                                                    )}
                                                    <div className="cl-avatar">
                                                        {cliente.nome.charAt(0).toUpperCase()}
                                                    </div>
                                                    <h3 title={cliente.nome}>{cliente.nome}</h3>
                                                </div>
                                                <div className="cl-card-actions">
                                                    <button onClick={() => handleOpenViewModal(cliente.id)} className="cl-icon-btn cl-view" title="Visualizar Detalhes"><FaEye /></button>
                                                    {hasAdminPermission && (
                                                        <>
                                                            <button onClick={() => handleOpenEditModal(cliente)} className="cl-icon-btn cl-edit" title="Editar"><FaEdit /></button>
                                                            <button onClick={() => handleDelete(cliente.id)} className="cl-icon-btn cl-delete" title="Excluir"><FaTrash /></button>
                                                        </>
                                                    )}
                                                </div>
                                            </header>

                                            <div className="cl-card-body">
                                                <div className="cl-info-row">
                                                    <FaIdCard className="cl-info-icon" /> 
                                                    <span>{cliente.cpf_cnpj || 'Documento não informado'}</span>
                                                </div>
                                                <div className="cl-info-row">
                                                    <FaPhone className="cl-info-icon" /> 
                                                    <span>{cliente.telefone || 'Telefone não informado'}</span>
                                                </div>
                                            </div>

                                            <footer className="cl-card-footer">
                                                <div className="cl-badge">
                                                    <FaTruck />
                                                    <span><strong>{cliente.total_servicos || 0}</strong> serviços</span>
                                                </div>
                                                <div className="cl-badge">
                                                    <FaCalendarCheck />
                                                    <span>{cliente.ultimo_servico ? format(new Date(cliente.ultimo_servico), 'dd/MM/yyyy', { locale: ptBR }) : 'Sem histórico'}</span>
                                                </div>
                                            </footer>
                                        </article>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="cl-table-wrapper animate-fade-in">
                                <table className="cl-table">
                                    <thead>
                                        <tr>
                                            {hasAdminPermission && <th width="40"></th>}
                                            <th>Cliente</th>
                                            <th>Contato</th>
                                            <th>Métricas</th>
                                            <th className="text-right">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {clientes.map(cliente => {
                                            const isSelected = selectedClients.includes(cliente.id);
                                            return (
                                                <tr key={cliente.id} className={isSelected ? 'selected-row' : ''}>
                                                    {hasAdminPermission && (
                                                        <td onClick={() => handleToggleSelect(cliente.id)} className="cl-table-checkbox">
                                                            {isSelected ? <FaCheckSquare className="checked" /> : <FaRegSquare />}
                                                        </td>
                                                    )}
                                                    <td>
                                                        <div className="cl-table-client-info">
                                                            <div className="cl-avatar-small">
                                                                {cliente.nome.charAt(0).toUpperCase()}
                                                            </div>
                                                            <div>
                                                                <strong>{cliente.nome}</strong>
                                                                <span className="cl-doc"><FaIdCard /> {cliente.cpf_cnpj || 'Não informado'}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="cl-table-contact">
                                                            <span><FaPhone /> {cliente.telefone || 'Sem telefone'}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="cl-table-metrics">
                                                            <span className="cl-badge"><FaTruck /> {cliente.total_servicos || 0} OS</span>
                                                            <span className="cl-badge"><FaCalendarCheck /> {cliente.ultimo_servico ? format(new Date(cliente.ultimo_servico), 'dd/MM/yyyy', { locale: ptBR }) : 'S/ histórico'}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="cl-table-actions">
                                                            <button onClick={() => handleOpenViewModal(cliente.id)} className="cl-icon-btn cl-view" title="Visualizar Detalhes"><FaEye /></button>
                                                            {hasAdminPermission && (
                                                                <>
                                                                    <button onClick={() => handleOpenEditModal(cliente)} className="cl-icon-btn cl-edit" title="Editar"><FaEdit /></button>
                                                                    <button onClick={() => handleDelete(cliente.id)} className="cl-icon-btn cl-delete" title="Excluir"><FaTrash /></button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* PAGINAÇÃO */}
                        {pagination.total > 0 && (
                            <nav className="cl-pagination">
                                <button onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page <= 1}>Anterior</button>
                                <span>Página <strong>{pagination.page}</strong> de <strong>{pagination.totalPages}</strong> <small>({pagination.total} registros)</small></span>
                                <button onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages}>Próxima</button>
                            </nav>
                        )}
                    </>
                )}
            </section>

            {/* MODAL ADICIONAR CLIENTE */}
            {isAddModalOpen && (
                <div className="cl-modal-overlay" onClick={handleCloseAddModal}>
                    <div className="cl-modal-box" onClick={e => e.stopPropagation()}>
                        <header className="cl-modal-header">
                            <h2><FaUserPlus /> Novo Cliente</h2>
                            <button type="button" className="cl-close-btn" onClick={handleCloseAddModal}><FaTimes /></button>
                        </header>
                        <form onSubmit={handleAddSubmit} className="cl-form">
                            <div className="cl-modal-body">
                                <div className="cl-form-group">
                                    <label>Nome / Razão Social <span className="cl-asterisk">*</span></label>
                                    <input type="text" name="nome" value={formData.nome} onChange={handleInputChange} required />
                                </div>
                                <div className="cl-form-row">
                                    <div className="cl-form-group">
                                        <label>CPF / CNPJ <span className="cl-asterisk">*</span></label>
                                        <input type="text" name="cpf_cnpj" value={formData.cpf_cnpj} onChange={handleInputChange} required />
                                    </div>
                                    <div className="cl-form-group">
                                        <label>Telefone</label>
                                        <input type="text" name="telefone" value={formData.telefone} onChange={handleInputChange} />
                                    </div>
                                </div>
                                <div className="cl-form-group">
                                    <label>Email</label>
                                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} />
                                </div>
                                <div className="cl-form-group">
                                    <label>Endereço Completo</label>
                                    <input type="text" name="endereco" value={formData.endereco} onChange={handleInputChange} />
                                </div>
                            </div>
                            <footer className="cl-modal-footer">
                                <button type="button" className="cl-btn cl-btn-secondary" onClick={handleCloseAddModal} disabled={isSubmitting}>Cancelar</button>
                                <button type="submit" className="cl-btn cl-btn-primary" disabled={isSubmitting}>
                                    {isSubmitting ? 'Salvando...' : 'Cadastrar Cliente'}
                                </button>
                            </footer>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL EDITAR CLIENTE */}
            {isEditModalOpen && editingCliente && (
                <div className="cl-modal-overlay" onClick={handleCloseEditModal}>
                    <div className="cl-modal-box" onClick={e => e.stopPropagation()}>
                        <header className="cl-modal-header">
                            <h2><FaEdit /> Editar Cliente</h2>
                            <button type="button" className="cl-close-btn" onClick={handleCloseEditModal}><FaTimes /></button>
                        </header>
                        <form onSubmit={handleUpdateSubmit} className="cl-form">
                            <div className="cl-modal-body">
                                <div className="cl-form-group">
                                    <label>Nome / Razão Social <span className="cl-asterisk">*</span></label>
                                    <input type="text" name="nome" value={editingCliente.nome} onChange={handleEditInputChange} required />
                                </div>
                                <div className="cl-form-row">
                                    <div className="cl-form-group">
                                        <label>CPF / CNPJ <span className="cl-asterisk">*</span></label>
                                        <input type="text" name="cpf_cnpj" value={editingCliente.cpf_cnpj} onChange={handleEditInputChange} required />
                                    </div>
                                    <div className="cl-form-group">
                                        <label>Telefone</label>
                                        <input type="text" name="telefone" value={editingCliente.telefone} onChange={handleEditInputChange} />
                                    </div>
                                </div>
                                <div className="cl-form-group">
                                    <label>Email</label>
                                    <input type="email" name="email" value={editingCliente.email} onChange={handleEditInputChange} />
                                </div>
                                <div className="cl-form-group">
                                    <label>Endereço Completo</label>
                                    <input type="text" name="endereco" value={editingCliente.endereco} onChange={handleEditInputChange} />
                                </div>
                            </div>
                            <footer className="cl-modal-footer">
                                <button type="button" className="cl-btn cl-btn-secondary" onClick={handleCloseEditModal} disabled={isSubmitting}>Cancelar</button>
                                <button type="submit" className="cl-btn cl-btn-primary" disabled={isSubmitting}>
                                    {isSubmitting ? 'Atualizando...' : 'Salvar Alterações'}
                                </button>
                            </footer>
                        </form>
                    </div>
                </div>
            )}

            {isViewModalOpen && (
                <ClienteDetalhesModal clienteId={viewingClientId} onClose={handleCloseViewModal} />
            )}
        </main>
    );
}